import { fieldOperationMapping } from "containers/ExecutionFilter/constants";
import { getFilterStorageKey } from "containers/ExecutionFilter/filterStorage";
import { getValuesFromQuery, useSetQueryString } from "containers/ExecutionFilter/utils";
import { useCallback, useMemo } from "react";
import { useHistory } from "react-router";
import { getSearchField, getSearchObj, parseFilterSafely } from "utils/utils";
import { initialFilter } from "./constants";

export const makeQueryString = (filterFromQuery: any, roleId?: string) => {
    const filters: any = { and: [] };

    let identityValue: string[] = [];

    if (filterFromQuery) {
        const filterOptions = parseFilterSafely(filterFromQuery);

        filterOptions.and = (filterOptions.and || []).map((field: any) => {
            if (field.field === "identity_type") {
                identityValue = field.value.map((value: string) =>
                    value.toUpperCase().replaceAll(" ", "_")
                );
                return {
                    field: "identity_id--identity.id:identity_type",
                    op: "in",
                    value: identityValue,
                };
            } else if (field.field === "name") {
                const preparedField = getSearchField({
                    term: field.value,
                    field: "identity_id--identity.id:name",
                    idFieldName: "identity_id--identity.id:id",
                });
                return preparedField;
            }
            return field;
        });

        filters.and.push(filterOptions);
    }

    if (roleId) {
        filters.and.push(
            getSearchObj({
                field: "role_id",
                op: "eq",
                value: roleId,
            })
        );
    }

    return filters.and ? JSON.stringify(filters) : null;
};

export const useIdentityRoleFilter = (
    queryParams: { [key: string]: string },
    roleId: string
) => {
    const history = useHistory();
    const filterStorageKey = getFilterStorageKey({ page: "role-identity", roleId });
    const initialFilterValues = {
        identity_type: initialFilter.identity_type,
    };

    const { setKeyValue } = useMemo(() => getValuesFromQuery(queryParams), [queryParams]);

    const {
        setQueryString,
        setInitialQueryString,
        effectiveQueryParams,
        hasStoredFilter,
    } = useSetQueryString(queryParams, history, filterStorageKey);

    const effectiveFilterHelpers = useMemo(
        () => getValuesFromQuery(effectiveQueryParams),
        [effectiveQueryParams]
    );

    const filteredValues: any = useMemo(
        () => ({
            name: effectiveFilterHelpers.findEncodedValueInFilterQuery("name"),
            identity_type:
                effectiveFilterHelpers.findValueInFilterQuery("identity_type").array ||
                initialFilterValues.identity_type,
        }),
        [effectiveFilterHelpers, initialFilterValues.identity_type]
    );

    const getFilters = useCallback(
        (filterSeed) => {
            const filters: any = [];

            for (const key in filterSeed) {
                const op = fieldOperationMapping[key];

                if (Array.isArray(filterSeed[key])) {
                    if (filterSeed[key].length) {
                        filters.push({
                            field: key,
                            op,
                            value: filterSeed[key],
                        });
                    }
                } else {
                    setKeyValue(filters, key, op, filterSeed[key] as string);
                }
            }

            return filters;
        },
        [setKeyValue]
    );

    const setInitialFilterHandler = useCallback(
        (historyMethod?: "push" | "replace") => {
            if (!hasStoredFilter) {
                setInitialQueryString(initialFilter, historyMethod);
            }
        },
        [hasStoredFilter, setInitialQueryString]
    );

    return {
        filteredValues,
        effectiveQueryParams,
        setInitialFilterHandler,
        setQueryString,
        getFilters,
    };
};
